import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';
import { toast } from 'react-toastify';
import {
  refetchGlobalSuppliersQuery,
  useCreateGlobalSupplierMutation,
  useUpdateGlobalSupplierMutation,
} from '../../generated/graphql';

const PUBLIC_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'live.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'seznam.cz',
  'email.cz',
  'centrum.cz',
  'volny.cz',
  'post.cz',
  'atlas.sk',
  'azet.sk',
  'pobox.sk',
]);

function getEmailDomain(email: string): string {
  return email.split('@')[1]?.toLowerCase() ?? '';
}

function isPublicDomain(email: string): boolean {
  return PUBLIC_EMAIL_DOMAINS.has(getEmailDomain(email));
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const AddSupplierModal = ({ open, onClose, onCreated }: Props) => {
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [invoicingEmail, setInvoicingEmail] = useState('');
  const [exceptionFlag, setExceptionFlag] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createGlobalSupplierMutation, { loading: creating }] =
    useCreateGlobalSupplierMutation({
      refetchQueries: [refetchGlobalSuppliersQuery()],
    });

  const [updateGlobalSupplierMutation, { loading: updatingException }] =
    useUpdateGlobalSupplierMutation({
      refetchQueries: [refetchGlobalSuppliersQuery()],
    });

  const submitting = creating || updatingException;

  const contactEmailDomain = getEmailDomain(contactEmail);
  const contactIsPublicDomain = contactEmail
    ? isPublicDomain(contactEmail)
    : false;
  const derivedEmailDomain =
    contactEmail && !contactIsPublicDomain ? contactEmailDomain : '';

  let contactEmailHint: string | undefined;
  if (contactEmail && !contactIsPublicDomain && contactEmailDomain) {
    contactEmailHint = `Email domain will be set to: ${contactEmailDomain}`;
  } else if (contactIsPublicDomain) {
    contactEmailHint =
      'This is a public email domain — invoicing email is required';
  }

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = 'Supplier name is required';
    if (!contactEmail.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      newErrors.contactEmail = 'Invalid email address';
    } else if (contactIsPublicDomain && !invoicingEmail.trim()) {
      newErrors.invoicingEmail =
        'Invoicing email is required when contact email uses a public domain';
    }
    if (exceptionFlag && !exceptionReason.trim()) {
      newErrors.exceptionReason =
        'A reason is required for suppliers excluded from automated email outreach';
    }
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const createResult = await createGlobalSupplierMutation({
        variables: {
          name: name.trim(),
          contactEmail: contactEmail.trim(),
          emailDomain: derivedEmailDomain || undefined,
          invoicingEmail: invoicingEmail.trim() || undefined,
        },
        toastConfig: null,
      });

      const newId = createResult?.data?.createGlobalSupplier?.id;
      if (!newId) {
        throw new Error('Create supplier did not return an id');
      }

      if (exceptionFlag) {
        await updateGlobalSupplierMutation({
          variables: {
            id: newId,
            exceptionFlag: true,
            exceptionReason: exceptionReason.trim(),
          },
          toastConfig: null,
        });
      }

      toast('Supplier created successfully', { type: 'success' });
      onCreated();
      handleClose();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to create supplier';
      toast(msg, { type: 'error' });
    }
  };

  const handleClose = () => {
    setName('');
    setContactEmail('');
    setInvoicingEmail('');
    setExceptionFlag(false);
    setExceptionReason('');
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add New Supplier
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3.5} sx={{ mt: 1 }}>
          <TextField
            label="Supplier Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrors((prev) => ({ ...prev, name: '' }));
            }}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
          />

          <TextField
            label="Contact Email"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              setErrors((prev) => ({
                ...prev,
                contactEmail: '',
                invoicingEmail: '',
              }));
            }}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail || contactEmailHint}
            fullWidth
            size="small"
          />

          {derivedEmailDomain && (
            <TextField
              label="Email Domain"
              value={derivedEmailDomain}
              disabled
              fullWidth
              size="small"
              helperText="Auto-derived from contact email"
            />
          )}

          <TextField
            label="Invoicing Email"
            value={invoicingEmail}
            onChange={(e) => {
              setInvoicingEmail(e.target.value);
              setErrors((prev) => ({ ...prev, invoicingEmail: '' }));
            }}
            error={!!errors.invoicingEmail}
            helperText={
              errors.invoicingEmail ||
              (contactIsPublicDomain
                ? 'Required: the email address invoices are actually sent from'
                : 'Optional: only needed for public-domain contact emails')
            }
            disabled={!contactIsPublicDomain && !!contactEmail}
            fullWidth
            size="small"
          />

          {!contactIsPublicDomain && contactEmail && (
            <Typography variant="caption" color="text.secondary">
              Invoicing email is not required when using a business email
              domain.
            </Typography>
          )}

          <Stack spacing={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exceptionFlag}
                  onChange={(e) => {
                    setExceptionFlag(e.target.checked);
                    setErrors((prev) => ({
                      ...prev,
                      exceptionReason: '',
                    }));
                  }}
                />
              }
              label="Excluded from automated email outreach"
            />
            <Typography variant="caption" color="text.secondary" pl={4}>
              Use when the supplier does not support these requests by email
              (e.g. direct contact or a web form only).
            </Typography>
          </Stack>

          {exceptionFlag && (
            <TextField
              label="Reason"
              value={exceptionReason}
              onChange={(e) => {
                setExceptionReason(e.target.value);
                setErrors((prev) => ({ ...prev, exceptionReason: '' }));
              }}
              error={!!errors.exceptionReason}
              helperText={
                errors.exceptionReason ||
                'Shown to admins when hovering disabled suppliers in integration requests'
              }
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create Supplier
        </Button>
      </DialogActions>
    </Dialog>
  );
};
